import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";
import useGameCatalog from "../hooks/useGameCatalog";
import {
  ROLE_LABELS,
  MODULE_ACCESS,
  MODULE_LABELS,
} from "../utils/adminRoles";

const normalizeAccess = (access, roleKeys) =>
  roleKeys.reduce((acc, roleKey) => {
    const list = Array.isArray(access?.[roleKey])
      ? access[roleKey]
      : MODULE_ACCESS[roleKey] || [];
    const baseline = Array.isArray(MODULE_ACCESS[roleKey])
      ? MODULE_ACCESS[roleKey]
      : [];
    const normalizedList =
      roleKey === "developer" ? [...baseline, ...list] : list;
    acc[roleKey] = Array.from(new Set(normalizedList)).sort();
    return acc;
  }, {});

const FORMAT_OPTIONS = [
  { value: "battle_royale", label: "Battle Royale" },
  { value: "team_bracket", label: "Team Bracket" },
  { value: "solo_bracket", label: "Solo Bracket" },
];

const ENTRY_TYPE_OPTIONS = [
  { value: "team", label: "Team" },
  { value: "player", label: "Player" },
];

const buildGameDraft = (games) =>
  (Array.isArray(games) ? games : []).map((game, index) => ({
    tempId: game.tempId || game.id || `game-${index + 1}`,
    isNew: Boolean(game.isNew),
    id: String(game.id || "").trim(),
    name: String(game.name || ""),
    format: game.format || "team_bracket",
    entryType: game.entryType || "team",
    order: Number.isFinite(Number(game.order)) ? Number(game.order) : index + 1,
    active: game.active !== false,
    imageUrl: game.imageUrl || "",
  }));

const serializeGameDraft = (games) =>
  (Array.isArray(games) ? games : []).map((game, index) => ({
    id: String(game.id || "").trim(),
    name: String(game.name || "").trim(),
    format: game.format || "team_bracket",
    entryType: game.entryType || "team",
    order: Number.isFinite(Number(game.order)) ? Number(game.order) : index + 1,
    active: game.active !== false,
    imageUrl: String(game.imageUrl || "").trim(),
  }));

const sanitizeFileName = (name = "icon") =>
  String(name).replace(/[^a-zA-Z0-9._-]/g, "_");

const BANNER_TYPE_OPTIONS = [
  { value: "none", label: "No link" },
  { value: "link", label: "External link" },
  { value: "route", label: "In-app navigation" },
];

const BANNER_ROUTE_GROUPS = [
  {
    label: "Main app",
    options: [
      { value: "Home", label: "Home" },
      { value: "Tournaments", label: "Tournaments" },
      { value: "TournamentDetails", label: "Tournament details" },
      { value: "Community", label: "Community" },
      { value: "CommunityDetails", label: "Community details" },
      { value: "CreateCommunity", label: "Create community" },
      { value: "CreatorProfile", label: "Creator profile" },
      { value: "CreatorChat", label: "Creator chat" },
      { value: "Profile", label: "Profile" },
      { value: "Wallet", label: "Wallet" },
      { value: "Kyc", label: "KYC" },
      { value: "Notifications", label: "Notifications" },
      { value: "CreateTeam", label: "Create team" },
      { value: "JoinTeam", label: "Join team" },
      { value: "AccountDeletion", label: "Account deletion" },
      { value: "avtarSelection", label: "Avatar selection" },
    ],
  },
  {
    label: "Onboarding",
    options: [
      { value: "completeprofile", label: "Complete profile" },
      { value: "bio", label: "Tell us about yourself" },
      { value: "addGame", label: "Pick favorite games" },
      { value: "gameDetails", label: "Game details" },
      { value: "welcomeUser", label: "Welcome user" },
    ],
  },
  {
    label: "Auth",
    options: [
      { value: "Welcome", label: "Welcome" },
      { value: "Login", label: "Login" },
      { value: "Register", label: "Register" },
      { value: "otpscreen", label: "OTP screen" },
    ],
  },
];

const normalizeRouteParamValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return "";
};

const parseBannerRouteParams = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const sortRouteOptions = (items) =>
  [...items].sort((left, right) =>
    String(left?.label || "").localeCompare(String(right?.label || ""), undefined, {
      sensitivity: "base",
    })
  );

const getBannerRouteSelectorConfig = (routeName, routeParamOptions) => {
  switch (routeName) {
    case "TournamentDetails":
      return {
        label: "Tournament",
        placeholder: "Select a tournament",
        helperText: "Choose a tournament by name to store its target ID.",
        options: routeParamOptions.tournaments,
        getSelectedValue: (params) =>
          normalizeRouteParamValue(
            params?.tournamentId,
            params?.tournamentID,
            params?.id,
            params?.tournament?.id
          ),
        buildParams: (value) =>
          value ? { tournamentId: value, tournamentID: value, id: value } : null,
      };
    case "CommunityDetails":
      return {
        label: "Community",
        placeholder: "Select a community",
        helperText: "Choose a community by name to store its target ID.",
        options: routeParamOptions.communities,
        getSelectedValue: (params) =>
          normalizeRouteParamValue(params?.communityID, params?.communityId),
        buildParams: (value) =>
          value ? { communityID: value, communityId: value } : null,
      };
    case "CreatorProfile":
      return {
        label: "Creator",
        placeholder: "Select a creator",
        helperText: "Choose a creator by name to store its target ID.",
        options: routeParamOptions.creators,
        getSelectedValue: (params) =>
          normalizeRouteParamValue(params?.creatorId, params?.creatorID),
        buildParams: (value) =>
          value ? { creatorId: value, creatorID: value } : null,
      };
    case "CreatorChat":
      return {
        label: "Creator",
        placeholder: "Select a creator",
        helperText: "Choose a creator to open their chat thread.",
        options: routeParamOptions.creators,
        getSelectedValue: (params) =>
          normalizeRouteParamValue(params?.creatorId, params?.creatorID),
        buildParams: (value) =>
          value ? { creatorId: value, creatorID: value } : null,
      };
    case "Notifications":
      return {
        label: "Notification tab",
        placeholder: "Select a tab",
        helperText: "Choose which tab opens first in notifications.",
        options: [
          { value: "notifications", label: "Notifications" },
          { value: "requests", label: "Requests" },
        ],
        getSelectedValue: (params) => {
          const tab = String(params?.initialTab || params?.tab || "").trim().toLowerCase();
          if (tab.startsWith("req")) return "requests";
          if (tab.startsWith("notif")) return "notifications";
          return "";
        },
        buildParams: (value) => (value ? { initialTab: value } : null),
      };
    default:
      return null;
  }
};

const hasRequiredBannerRouteParams = (routeName, routeParams) => {
  switch (routeName) {
    case "TournamentDetails":
      return Boolean(
        routeParams?.tournamentId ||
          routeParams?.tournamentID ||
          routeParams?.id ||
          routeParams?.tournament?.id
      );
    case "CommunityDetails":
      return Boolean(routeParams?.communityID || routeParams?.communityId);
    case "CreatorProfile":
    case "CreatorChat":
      return Boolean(routeParams?.creatorId || routeParams?.creatorID);
    case "Notifications":
      return Boolean(routeParams?.initialTab || routeParams?.tab);
    default:
      return true;
  }
};

const normalizeBannerParams = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return "";
  }
};

const buildBannerDraft = (banners) =>
  (Array.isArray(banners) ? banners : []).map((banner, index) => {
    const idValue = String(banner?.id || `banner-${index + 1}`).trim();
    return {
      tempId: banner?.tempId || idValue || `banner-${index + 1}`,
      id: idValue,
      title: String(banner?.title || ""),
      imageUrl: String(banner?.imageUrl || banner?.image || ""),
      type: banner?.type || "none",
      linkUrl: String(banner?.linkUrl || banner?.url || ""),
      routeName: String(banner?.routeName || banner?.route || ""),
      routeParams: normalizeBannerParams(banner?.routeParams),
      order: Number.isFinite(Number(banner?.order))
        ? Number(banner.order)
        : index + 1,
      active: banner?.active !== false,
    };
  });

const serializeBannerDraft = (banners) =>
  (Array.isArray(banners) ? banners : []).map((banner, index) => ({
    id: String(banner?.id || "").trim(),
    title: String(banner?.title || "").trim(),
    imageUrl: String(banner?.imageUrl || "").trim(),
    type: banner?.type || "none",
    linkUrl: String(banner?.linkUrl || "").trim(),
    routeName: String(banner?.routeName || "").trim(),
    routeParams:
      typeof banner?.routeParams === "string"
        ? banner.routeParams.trim()
        : "",
    order: Number.isFinite(Number(banner?.order))
      ? Number(banner.order)
      : index + 1,
    active: banner?.active !== false,
  }));

const buildBannerPayload = (banners) => {
  const normalized = serializeBannerDraft(banners);
  const payload = normalized.map((banner, index) => {
    let routeParams = null;
    if (banner.type === "route" && banner.routeParams) {
      try {
        routeParams = JSON.parse(banner.routeParams);
      } catch (error) {
        const label = banner.id || `#${index + 1}`;
        throw new Error(`Invalid JSON for banner ${label}.`);
      }
    }

    return {
      id: banner.id || `banner-${index + 1}`,
      title: banner.title || null,
      imageUrl: banner.imageUrl || null,
      type: banner.type || "none",
      linkUrl: banner.type === "link" ? banner.linkUrl || null : null,
      routeName: banner.type === "route" ? banner.routeName || null : null,
      routeParams: banner.type === "route" ? routeParams : null,
      order: Number.isFinite(Number(banner.order)) ? Number(banner.order) : 0,
      active: banner.active !== false,
    };
  });

  return payload.sort((a, b) => (a.order || 0) - (b.order || 0));
};

const DEFAULT_HOME_CONFIG = {
  heroFirst: true,
  hero: {
    badge: "LIVE",
    label: "Tournament queue",
    titleLineOne: "Tournaments are live, {greeting}.",
    titleLineTwo: "Claim your next bracket slot.",
    subtitle:
      "Lock in your entry before registrations close, then squad up with your community.",
    primaryActionLabel: "Play tournaments",
    secondaryActionLabel: "Join a community",
  },
};

const buildHomeConfigDraft = (config) => {
  const hero = config?.hero || {};
  return {
    heroFirst: config?.heroFirst !== false,
    badge: String(hero?.badge || DEFAULT_HOME_CONFIG.hero.badge),
    label: String(hero?.label || DEFAULT_HOME_CONFIG.hero.label),
    titleLineOne: String(
      hero?.titleLineOne || DEFAULT_HOME_CONFIG.hero.titleLineOne
    ),
    titleLineTwo: String(
      hero?.titleLineTwo || DEFAULT_HOME_CONFIG.hero.titleLineTwo
    ),
    subtitle: String(hero?.subtitle || DEFAULT_HOME_CONFIG.hero.subtitle),
    primaryActionLabel: String(
      hero?.primaryActionLabel || DEFAULT_HOME_CONFIG.hero.primaryActionLabel
    ),
    secondaryActionLabel: String(
      hero?.secondaryActionLabel ||
        DEFAULT_HOME_CONFIG.hero.secondaryActionLabel
    ),
  };
};

const serializeHomeConfigDraft = (draft) => ({
  heroFirst: draft?.heroFirst !== false,
  badge: String(draft?.badge || "").trim(),
  label: String(draft?.label || "").trim(),
  titleLineOne: String(draft?.titleLineOne || "").trim(),
  titleLineTwo: String(draft?.titleLineTwo || "").trim(),
  subtitle: String(draft?.subtitle || "").trim(),
  primaryActionLabel: String(draft?.primaryActionLabel || "").trim(),
  secondaryActionLabel: String(draft?.secondaryActionLabel || "").trim(),
});

const buildHomeConfigPayload = (draft) => {
  const normalized = serializeHomeConfigDraft(draft);
  return {
    heroFirst: normalized.heroFirst,
    hero: {
      badge: normalized.badge || DEFAULT_HOME_CONFIG.hero.badge,
      label: normalized.label || DEFAULT_HOME_CONFIG.hero.label,
      titleLineOne:
        normalized.titleLineOne || DEFAULT_HOME_CONFIG.hero.titleLineOne,
      titleLineTwo:
        normalized.titleLineTwo || DEFAULT_HOME_CONFIG.hero.titleLineTwo,
      subtitle: normalized.subtitle || DEFAULT_HOME_CONFIG.hero.subtitle,
      primaryActionLabel:
        normalized.primaryActionLabel ||
        DEFAULT_HOME_CONFIG.hero.primaryActionLabel,
      secondaryActionLabel:
        normalized.secondaryActionLabel ||
        DEFAULT_HOME_CONFIG.hero.secondaryActionLabel,
    },
  };
};

const Settings = () => {
  const {
    profile,
    refreshProfile,
    role,
    user,
    moduleAccess,
    refreshModuleAccess,
  } = useAdminAuth();
  const {
    games,
    loading: gamesLoading,
    error: gamesLoadError,
    refreshGames,
    saveGames,
  } = useGameCatalog();
  const [accessDraft, setAccessDraft] = useState(
    moduleAccess || MODULE_ACCESS
  );
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState("");
  const canEditAccess = role === "developer";

  const [gameDraft, setGameDraft] = useState([]);
  const [gamesInitialized, setGamesInitialized] = useState(false);
  const [savingGames, setSavingGames] = useState(false);
  const [gamesError, setGamesError] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [iconUploadProgress, setIconUploadProgress] = useState({});
  const [bannerDraft, setBannerDraft] = useState([]);
  const [bannerBase, setBannerBase] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [savingBanners, setSavingBanners] = useState(false);
  const [bannersError, setBannersError] = useState("");
  const [bannerUploadProgress, setBannerUploadProgress] = useState({});
  const [routeParamOptions, setRouteParamOptions] = useState({
    tournaments: [],
    communities: [],
    creators: [],
  });
  const [routeParamOptionsLoading, setRouteParamOptionsLoading] = useState(false);
  const [routeParamOptionsError, setRouteParamOptionsError] = useState("");
  const [homeConfigDraft, setHomeConfigDraft] = useState(
    buildHomeConfigDraft(DEFAULT_HOME_CONFIG)
  );
  const [homeConfigBase, setHomeConfigBase] = useState(
    buildHomeConfigDraft(DEFAULT_HOME_CONFIG)
  );
  const [homeConfigLoading, setHomeConfigLoading] = useState(false);
  const [savingHomeConfig, setSavingHomeConfig] = useState(false);
  const [homeConfigError, setHomeConfigError] = useState("");

  const roleKeys = useMemo(() => Object.keys(ROLE_LABELS), []);
  const moduleKeys = useMemo(() => Object.keys(MODULE_LABELS), []);
  const canEditHomeConfig =
    role === "developer" || role === "super_admin";
  const tabs = useMemo(() => {
    const list = [
      { id: "general", label: "General" },
      { id: "advertise", label: "Advertise" },
    ];
    if (canEditAccess) {
      list.push({ id: "games", label: "Game catalog" });
    }
    return list;
  }, [canEditAccess]);
  const baseGamesSerialized = useMemo(
    () => JSON.stringify(serializeGameDraft(games)),
    [games]
  );
  const draftGamesSerialized = useMemo(
    () => JSON.stringify(serializeGameDraft(gameDraft)),
    [gameDraft]
  );
  const isGamesDirty = baseGamesSerialized !== draftGamesSerialized;
  const hasUploadingIcon = useMemo(
    () => Object.keys(iconUploadProgress).length > 0,
    [iconUploadProgress]
  );
  const baseBannersSerialized = useMemo(
    () => JSON.stringify(serializeBannerDraft(bannerBase)),
    [bannerBase]
  );
  const draftBannersSerialized = useMemo(
    () => JSON.stringify(serializeBannerDraft(bannerDraft)),
    [bannerDraft]
  );
  const isBannersDirty = baseBannersSerialized !== draftBannersSerialized;
  const hasUploadingBanner = useMemo(
    () => Object.keys(bannerUploadProgress).length > 0,
    [bannerUploadProgress]
  );
  const baseHomeConfigSerialized = useMemo(
    () => JSON.stringify(serializeHomeConfigDraft(homeConfigBase)),
    [homeConfigBase]
  );
  const draftHomeConfigSerialized = useMemo(
    () => JSON.stringify(serializeHomeConfigDraft(homeConfigDraft)),
    [homeConfigDraft]
  );
  const isHomeConfigDirty =
    baseHomeConfigSerialized !== draftHomeConfigSerialized;

  useEffect(() => {
    setAccessDraft(moduleAccess || MODULE_ACCESS);
  }, [moduleAccess]);

  useEffect(() => {
    if (!gamesInitialized && games.length) {
      setGameDraft(buildGameDraft(games));
      setGamesInitialized(true);
    }
  }, [games, gamesInitialized]);

  useEffect(() => {
    if (!gamesInitialized || !games.length || isGamesDirty) return;
    setGameDraft(buildGameDraft(games));
  }, [games, gamesInitialized, isGamesDirty]);

  useEffect(() => {
    if (tabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(tabs[0]?.id || "general");
  }, [activeTab, tabs]);

  const loadBanners = useCallback(async () => {
    setBannersLoading(true);
    setBannersError("");
    try {
      const configRef = doc(db, "config", "banners");
      const snap = await getDoc(configRef);
      const data = snap.exists() ? snap.data() : {};
      const raw = Array.isArray(data?.banners) ? data.banners : [];
      const draft = buildBannerDraft(raw);
      setBannerDraft(draft);
      setBannerBase(draft);
    } catch (error) {
      console.error("Failed to load banners", error);
      setBannersError("Failed to load banners.");
    } finally {
      setBannersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const loadBannerRouteOptions = useCallback(async () => {
    setRouteParamOptionsLoading(true);
    setRouteParamOptionsError("");
    try {
      const [tournamentsSnap, communitiesSnap, creatorsSnap] = await Promise.all([
        getDocs(collection(db, "tournaments")),
        getDocs(collection(db, "communities")),
        getDocs(collection(db, "creators")),
      ]);

      const tournaments = sortRouteOptions(
        tournamentsSnap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            value: docSnap.id,
            label:
              String(
                data.tournamentName ||
                  data.name ||
                  data.title ||
                  docSnap.id
              ).trim() || docSnap.id,
          };
        })
      );

      const communities = sortRouteOptions(
        communitiesSnap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            value: docSnap.id,
            label: String(data.name || data.title || docSnap.id).trim() || docSnap.id,
          };
        })
      );

      const creators = sortRouteOptions(
        creatorsSnap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            value: docSnap.id,
            label:
              String(
                data.displayName ||
                  data.name ||
                  data.fullName ||
                  data.username ||
                  docSnap.id
              ).trim() || docSnap.id,
          };
        })
      );

      setRouteParamOptions({ tournaments, communities, creators });
    } catch (error) {
      console.error("Failed to load banner route options", error);
      setRouteParamOptionsError("Failed to load route options.");
    } finally {
      setRouteParamOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBannerRouteOptions();
  }, [loadBannerRouteOptions]);

  const loadHomeConfig = useCallback(async () => {
    setHomeConfigLoading(true);
    setHomeConfigError("");
    try {
      const configRef = doc(db, "config", "home");
      const snap = await getDoc(configRef);
      const data = snap.exists() ? snap.data() || {} : DEFAULT_HOME_CONFIG;
      const draft = buildHomeConfigDraft(data);
      setHomeConfigDraft(draft);
      setHomeConfigBase(draft);
    } catch (error) {
      console.error("Failed to load home config", error);
      setHomeConfigError("Failed to load home config.");
    } finally {
      setHomeConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHomeConfig();
  }, [loadHomeConfig]);

  const isDirty = useMemo(() => {
    const baseline = normalizeAccess(moduleAccess || MODULE_ACCESS, roleKeys);
    const current = normalizeAccess(accessDraft, roleKeys);
    return JSON.stringify(baseline) !== JSON.stringify(current);
  }, [accessDraft, moduleAccess, roleKeys]);

  const toggleModuleAccess = (roleKey, moduleId) => {
    if (!canEditAccess || roleKey === "developer") return;
    setAccessDraft((prev) => {
      const current = new Set(
        Array.isArray(prev?.[roleKey])
          ? prev[roleKey]
          : MODULE_ACCESS[roleKey] || []
      );
      if (current.has(moduleId)) {
        current.delete(moduleId);
      } else {
        current.add(moduleId);
      }
      return {
        ...prev,
        [roleKey]: Array.from(current),
      };
    });
  };

  const handleSaveAccess = async () => {
    if (!canEditAccess || !user) return;
    setSavingAccess(true);
    setAccessError("");
    try {
      const settingsRef = doc(db, "adminSettings", "moduleAccess");
      const rolesPayload = normalizeAccess(accessDraft, roleKeys);
      await setDoc(
        settingsRef,
        {
          roles: rolesPayload,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        },
        { merge: true }
      );
      await refreshModuleAccess();
    } catch (error) {
      console.error("Failed to update module access", error);
      setAccessError("Failed to update module access.");
    } finally {
      setSavingAccess(false);
    }
  };

  const handleResetAccess = () => {
    setAccessDraft(moduleAccess || MODULE_ACCESS);
    setAccessError("");
  };

  const handleAddGame = () => {
    setGameDraft((prev) => {
      const maxOrder = prev.reduce((acc, item) => {
        const order = Number(item.order) || 0;
        return order > acc ? order : acc;
      }, 0);
      return [
        ...prev,
        {
          tempId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          isNew: true,
          id: "",
          name: "",
          format: "team_bracket",
          entryType: "team",
          order: maxOrder + 1,
          active: true,
          imageUrl: "",
        },
      ];
    });
  };

  const handleUpdateGame = (tempId, field, value) => {
    setGameDraft((prev) =>
      prev.map((game) =>
        game.tempId === tempId ? { ...game, [field]: value } : game
      )
    );
  };

  const handleRemoveGame = (tempId) => {
    setGameDraft((prev) => prev.filter((game) => game.tempId !== tempId));
  };

  const handleResetGames = () => {
    setGameDraft(buildGameDraft(games));
    setGamesError("");
  };

  const handleAddBanner = () => {
    const seed = Date.now().toString(36);
    const id = `banner-${seed}`;
    setBannerDraft((prev) => {
      const maxOrder = prev.reduce((acc, item) => {
        const order = Number(item.order) || 0;
        return order > acc ? order : acc;
      }, 0);
      return [
        ...prev,
        {
          tempId: id,
          id,
          title: "",
          imageUrl: "",
          type: "none",
          linkUrl: "",
          routeName: "",
          routeParams: "",
          order: maxOrder + 1,
          active: true,
        },
      ];
    });
  };

  const handleUpdateBanner = (tempId, field, value) => {
    setBannerDraft((prev) =>
      prev.map((banner) =>
        banner.tempId === tempId ? { ...banner, [field]: value } : banner
      )
    );
  };

  const handleRemoveBanner = (tempId) => {
    setBannerDraft((prev) => prev.filter((banner) => banner.tempId !== tempId));
  };

  const handleResetBanners = () => {
    setBannerDraft(buildBannerDraft(bannerBase));
    setBannersError("");
  };

  const handleUpdateBannerRouteName = (tempId, routeName) => {
    setBannerDraft((prev) =>
      prev.map((banner) =>
        banner.tempId === tempId
          ? {
              ...banner,
              routeName,
              routeParams: "",
            }
          : banner
      )
    );
  };

  const handleSelectBannerRouteParam = (tempId, routeName, value) => {
    const selectorConfig = getBannerRouteSelectorConfig(routeName, routeParamOptions);
    const routeParams = selectorConfig?.buildParams?.(value) || null;
    setBannerDraft((prev) =>
      prev.map((banner) =>
        banner.tempId === tempId
          ? {
              ...banner,
              routeParams: routeParams ? normalizeBannerParams(routeParams) : "",
            }
          : banner
      )
    );
  };

  const handleUpdateHomeConfig = (field, value) => {
    setHomeConfigDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleHeroPosition = () => {
    setHomeConfigDraft((prev) => ({
      ...prev,
      heroFirst: !prev.heroFirst,
    }));
  };

  const handleResetHomeConfig = () => {
    setHomeConfigDraft(buildHomeConfigDraft(homeConfigBase));
    setHomeConfigError("");
  };

  const handleBannerUpload = (tempId, file) => {
    if (!file) return;
    const banner = bannerDraft.find((item) => item.tempId === tempId);
    const bannerId = String(banner?.id || tempId || "banner");
    const safeName = sanitizeFileName(file.name);
    const filePath = `app-banners/${bannerId}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, filePath);

    setBannersError("");
    setBannerUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setBannerUploadProgress((prev) => ({
            ...prev,
            [tempId]: percent,
          }));
        }
      },
      (error) => {
        console.error("Failed to upload banner image", error);
        setBannersError("Failed to upload banner image.");
        setBannerUploadProgress((prev) => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          setBannerDraft((prev) =>
            prev.map((item) =>
              item.tempId === tempId ? { ...item, imageUrl: url } : item
            )
          );
        } catch (error) {
          console.error("Failed to fetch banner URL", error);
          setBannersError("Failed to fetch banner URL.");
        } finally {
          setBannerUploadProgress((prev) => {
            const next = { ...prev };
            delete next[tempId];
            return next;
          });
        }
      }
    );
  };

  const handleBannerFileChange = (tempId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleBannerUpload(tempId, file);
    event.target.value = "";
  };

  const handleSaveBanners = async () => {
    if (!user) return;
    setSavingBanners(true);
    setBannersError("");
    try {
      const payload = buildBannerPayload(bannerDraft);
      const missingImage = payload.find((banner) => !banner.imageUrl);
      if (missingImage) {
        setBannersError("Every banner needs an image.");
        setSavingBanners(false);
        return;
      }
      const missingLink = payload.find(
        (banner) => banner.type === "link" && !banner.linkUrl
      );
      if (missingLink) {
        setBannersError("Link banners must include a URL.");
        setSavingBanners(false);
        return;
      }
      const missingRoute = payload.find(
        (banner) => banner.type === "route" && !banner.routeName
      );
      if (missingRoute) {
        setBannersError("In-app banners must include a route name.");
        setSavingBanners(false);
        return;
      }
      const missingRouteParams = payload.find(
        (banner) =>
          banner.type === "route" &&
          !hasRequiredBannerRouteParams(banner.routeName, banner.routeParams || {})
      );
      if (missingRouteParams) {
        setBannersError(
          `Missing route target for banner ${missingRouteParams.id || "item"}.`
        );
        setSavingBanners(false);
        return;
      }

      await setDoc(
        doc(db, "config", "banners"),
        {
          banners: payload,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        },
        { merge: true }
      );
      const draft = buildBannerDraft(payload);
      setBannerDraft(draft);
      setBannerBase(draft);
    } catch (error) {
      console.error("Failed to save banners", error);
      setBannersError(error?.message || "Failed to save banners.");
    } finally {
      setSavingBanners(false);
    }
  };

  const handleSaveHomeConfig = async () => {
    if (!user || !canEditHomeConfig) return;
    setSavingHomeConfig(true);
    setHomeConfigError("");
    try {
      const payload = buildHomeConfigPayload(homeConfigDraft);
      await setDoc(
        doc(db, "config", "home"),
        {
          heroFirst: payload.heroFirst,
          hero: payload.hero,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
          updatedByRole: role || null,
        },
        { merge: true }
      );
      const draft = buildHomeConfigDraft(payload);
      setHomeConfigDraft(draft);
      setHomeConfigBase(draft);
    } catch (error) {
      console.error("Failed to save home config", error);
      setHomeConfigError(error?.message || "Failed to save home config.");
    } finally {
      setSavingHomeConfig(false);
    }
  };

  const handleIconUpload = (tempId, file) => {
    if (!file) return;
    const game = gameDraft.find((item) => item.tempId === tempId);
    const gameId = String(game?.id || tempId || "game");
    const safeName = sanitizeFileName(file.name);
    const filePath = `game-icons/${gameId}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, filePath);

    setGamesError("");
    setIconUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setIconUploadProgress((prev) => ({
            ...prev,
            [tempId]: percent,
          }));
        }
      },
      (error) => {
        console.error("Failed to upload icon", error);
        setGamesError("Failed to upload icon.");
        setIconUploadProgress((prev) => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          setGameDraft((prev) =>
            prev.map((item) =>
              item.tempId === tempId ? { ...item, imageUrl: url } : item
            )
          );
        } catch (error) {
          console.error("Failed to fetch icon URL", error);
          setGamesError("Failed to fetch icon URL.");
        } finally {
          setIconUploadProgress((prev) => {
            const next = { ...prev };
            delete next[tempId];
            return next;
          });
        }
      }
    );
  };

  const handleIconFileChange = (tempId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleIconUpload(tempId, file);
    event.target.value = "";
  };

  const handleSaveGames = async () => {
    if (!user) return;
    setSavingGames(true);
    setGamesError("");
    try {
      const cleaned = serializeGameDraft(gameDraft);
      const missingFields = cleaned.find((game) => !game.id || !game.name);
      if (missingFields) {
        setGamesError("Every game must have an ID and name.");
        setSavingGames(false);
        return;
      }
      const seen = new Set();
      const duplicate = cleaned.find((game) => {
        if (seen.has(game.id)) return true;
        seen.add(game.id);
        return false;
      });
      if (duplicate) {
        setGamesError(`Duplicate game ID detected: ${duplicate.id}`);
        setSavingGames(false);
        return;
      }
      const payload = cleaned.map((game) => ({
        ...game,
        imageUrl: game.imageUrl || null,
      }));
      const saved = await saveGames(payload, { updatedBy: user.uid });
      setGameDraft(buildGameDraft(saved));
    } catch (error) {
      console.error("Failed to save game catalog", error);
      setGamesError("Failed to save game catalog.");
    } finally {
      setSavingGames(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Administrative access and platform configuration.</p>
        </div>
        <div className="page-actions">
          <button
            className="button ghost"
            type="button"
            onClick={refreshProfile}
          >
            Refresh profile
          </button>
        </div>
      </div>

      <div className="manage-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`manage-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "general" ? (
        <div className="grid-2">
          <div className="card">
            <div className="card-header">
              <h3>Module access</h3>
              {canEditAccess ? (
                <div className="inline-actions">
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={handleResetAccess}
                    disabled={!isDirty || savingAccess}
                  >
                    Reset
                  </button>
                  <button
                    className="button primary small"
                    type="button"
                    onClick={handleSaveAccess}
                    disabled={!isDirty || savingAccess}
                  >
                    {savingAccess ? "Saving..." : "Save"}
                  </button>
                </div>
              ) : null}
            </div>
            {accessError ? (
              <div className="alert error">{accessError}</div>
            ) : null}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    {moduleKeys.map((moduleKey) => (
                      <th key={moduleKey} title={MODULE_LABELS[moduleKey]}>
                        {MODULE_LABELS[moduleKey]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roleKeys.map((roleKey) => {
                    const modules = Array.isArray(accessDraft?.[roleKey])
                      ? accessDraft[roleKey]
                      : MODULE_ACCESS[roleKey] || [];
                    const isLocked = roleKey === "developer";
                    return (
                      <tr key={roleKey}>
                        <td>
                          <div className="table-title">
                            {ROLE_LABELS[roleKey]}
                          </div>
                          <div className="table-subtitle">{roleKey}</div>
                        </td>
                        {moduleKeys.map((moduleKey) => (
                          <td key={`${roleKey}-${moduleKey}`}>
                            <input
                              type="checkbox"
                              checked={modules.includes(moduleKey)}
                              disabled={!canEditAccess || isLocked}
                              onChange={() =>
                                toggleModuleAccess(roleKey, moduleKey)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {canEditAccess ? (
              <div className="helper-text">
                Developer role is locked to prevent accidental lockouts.
              </div>
            ) : null}
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Profile snapshot</h3>
            </div>
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">Username</div>
                <div className="detail-value">
                  {profile?.username || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Admin Role</div>
                <div className="detail-value">
                  {ROLE_LABELS[profile?.adminRole] ||
                    profile?.adminRole ||
                    "None"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Admin Enabled</div>
                <div className="detail-value">
                  {profile?.adminEnabled === false ? "No" : "Yes"}
                </div>
              </div>
              <div className="divider" />
              <div className="helper-text">
                Use the Users module to assign roles and update admin access.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "advertise" ? (
        <div className="manage-column">
          <div className="card">
            <div className="card-header">
              <div>
                <h3>Home hero</h3>
                <p className="helper-text">
                  Swap the top slot between the hero and spotlight sections, and edit hero copy.
                </p>
              </div>
              <div className="inline-actions">
                <button
                  className="button ghost small"
                  type="button"
                  onClick={loadHomeConfig}
                  disabled={homeConfigLoading}
                >
                  Refresh
                </button>
                <button
                  className="button ghost small"
                  type="button"
                  onClick={handleResetHomeConfig}
                  disabled={!isHomeConfigDirty || savingHomeConfig}
                >
                  Reset
                </button>
                <button
                  className="button primary small"
                  type="button"
                  onClick={handleSaveHomeConfig}
                  disabled={
                    !canEditHomeConfig ||
                    !isHomeConfigDirty ||
                    savingHomeConfig
                  }
                >
                  {savingHomeConfig ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
            {homeConfigError ? (
              <div className="alert error">{homeConfigError}</div>
            ) : null}
            {homeConfigLoading ? (
              <div className="helper-text">Loading home hero config...</div>
            ) : (
              <>
                <div className="detail-body">
                  <div className="detail-row">
                    <div className="detail-label">Current top slot</div>
                    <div className="detail-value">
                      {homeConfigDraft.heroFirst ? "Hero" : "Featured spotlight"}
                    </div>
                  </div>
                  <div className="helper-text">
                    Only Developer and Super Admin can change this layout. Use
                    `{`{greeting}`}` inside title line 1 to inject the user’s name.
                  </div>
                </div>
                <div className="divider" />
                <div className="inline-actions">
                  <button
                    className="button ghost"
                    type="button"
                    onClick={handleToggleHeroPosition}
                    disabled={!canEditHomeConfig || savingHomeConfig}
                  >
                    {homeConfigDraft.heroFirst
                      ? "Move spotlight to top"
                      : "Move hero to top"}
                  </button>
                </div>
                <div className="divider" />
                <div className="form-grid">
                  <label className="field">
                    <span>Badge</span>
                    <input
                      type="text"
                      value={homeConfigDraft.badge}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig("badge", event.target.value)
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Label</span>
                    <input
                      type="text"
                      value={homeConfigDraft.label}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig("label", event.target.value)
                      }
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Title Line 1</span>
                    <input
                      type="text"
                      value={homeConfigDraft.titleLineOne}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig("titleLineOne", event.target.value)
                      }
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Title Line 2</span>
                    <input
                      type="text"
                      value={homeConfigDraft.titleLineTwo}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig("titleLineTwo", event.target.value)
                      }
                    />
                  </label>
                  <label className="field field-wide">
                    <span>Subtitle</span>
                    <textarea
                      value={homeConfigDraft.subtitle}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig("subtitle", event.target.value)
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Primary Button</span>
                    <input
                      type="text"
                      value={homeConfigDraft.primaryActionLabel}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig(
                          "primaryActionLabel",
                          event.target.value
                        )
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Secondary Button</span>
                    <input
                      type="text"
                      value={homeConfigDraft.secondaryActionLabel}
                      disabled={!canEditHomeConfig || savingHomeConfig}
                      onChange={(event) =>
                        handleUpdateHomeConfig(
                          "secondaryActionLabel",
                          event.target.value
                        )
                      }
                    />
                  </label>
                </div>
              </>
            )}
          </div>

          <div className="card">
          <div className="card-header">
            <div>
              <h3>Advertise banners</h3>
              <p className="helper-text">
                Manage homepage banners with no link, external link, or in-app navigation.
              </p>
            </div>
            <div className="inline-actions">
              <button
                className="button ghost small"
                type="button"
                onClick={loadBanners}
                disabled={bannersLoading}
              >
                Refresh
              </button>
              <button
                className="button ghost small"
                type="button"
                onClick={handleResetBanners}
                disabled={!isBannersDirty || savingBanners || hasUploadingBanner}
              >
                Reset
              </button>
              <button
                className="button primary small"
                type="button"
                onClick={handleSaveBanners}
                disabled={!isBannersDirty || savingBanners || hasUploadingBanner}
              >
                {savingBanners ? "Saving..." : "Save"}
              </button>
              <button
                className="button small"
                type="button"
                onClick={handleAddBanner}
              >
                Add banner
              </button>
            </div>
          </div>
          {bannersError ? <div className="alert error">{bannersError}</div> : null}
          {bannersLoading && !bannerDraft.length ? (
            <div className="helper-text">Loading banners...</div>
          ) : (
            <div className="table-wrap">
              <table className="table banner-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th>Order</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Target</th>
                    <th>Image</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bannerDraft.map((banner) => {
                    const routeSelector = getBannerRouteSelectorConfig(
                      banner.routeName,
                      routeParamOptions
                    );
                    const parsedRouteParams = parseBannerRouteParams(
                      banner.routeParams
                    );
                    const selectedRouteParamValue = routeSelector?.getSelectedValue
                      ? routeSelector.getSelectedValue(parsedRouteParams || {})
                      : "";

                    return (
                    <tr key={banner.tempId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={banner.active !== false}
                          onChange={(event) =>
                            handleUpdateBanner(
                              banner.tempId,
                              "active",
                              event.target.checked
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={banner.order}
                          onChange={(event) =>
                            handleUpdateBanner(
                              banner.tempId,
                              "order",
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="banner-fields">
                          <input
                            type="text"
                            value={banner.title}
                            placeholder="Banner title"
                            onChange={(event) =>
                              handleUpdateBanner(
                                banner.tempId,
                                "title",
                                event.target.value
                              )
                            }
                          />
                          <div className="table-subtitle">
                            {banner.id || "New banner"}
                          </div>
                        </div>
                      </td>
                      <td>
                        <select
                          value={banner.type}
                          onChange={(event) =>
                            handleUpdateBanner(
                              banner.tempId,
                              "type",
                              event.target.value
                            )
                          }
                        >
                          {BANNER_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="banner-target">
                          {banner.type === "link" ? (
                            <input
                              type="url"
                              value={banner.linkUrl}
                              placeholder="https://example.com"
                              onChange={(event) =>
                                handleUpdateBanner(
                                  banner.tempId,
                                  "linkUrl",
                                  event.target.value
                                )
                              }
                            />
                          ) : banner.type === "route" ? (
                            <>
                              <select
                                value={banner.routeName}
                                onChange={(event) =>
                                  handleUpdateBannerRouteName(
                                    banner.tempId,
                                    event.target.value
                                  )
                                }
                              >
                                <option value="">Select a route</option>
                                {BANNER_ROUTE_GROUPS.map((group) => (
                                  <optgroup key={group.label} label={group.label}>
                                    {group.options.map((route) => (
                                      <option key={route.value} value={route.value}>
                                        {route.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              {routeSelector ? (
                                <>
                                  <select
                                    value={selectedRouteParamValue}
                                    disabled={routeParamOptionsLoading}
                                    onChange={(event) =>
                                      handleSelectBannerRouteParam(
                                        banner.tempId,
                                        banner.routeName,
                                        event.target.value
                                      )
                                    }
                                  >
                                    <option value="">
                                      {routeParamOptionsLoading
                                        ? `Loading ${routeSelector.label.toLowerCase()}s...`
                                        : routeSelector.placeholder}
                                    </option>
                                    {routeSelector.options.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="helper-text">
                                    {routeParamOptionsError ||
                                      routeSelector.helperText}
                                  </div>
                                </>
                              ) : null}
                              <textarea
                                value={banner.routeParams}
                                placeholder={
                                  routeSelector
                                    ? 'Advanced route params JSON (optional override)'
                                    : 'Route params JSON (e.g. {"id":"123"})'
                                }
                                onChange={(event) =>
                                  handleUpdateBanner(
                                    banner.tempId,
                                    "routeParams",
                                    event.target.value
                                  )
                                }
                              />
                            </>
                          ) : (
                            <span className="banner-note">No action</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="banner-cell">
                          <div className="banner-preview">
                            {banner.imageUrl ? (
                              <img src={banner.imageUrl} alt="" />
                            ) : (
                              <span className="icon-placeholder">No image</span>
                            )}
                          </div>
                          <div className="icon-actions">
                            <label className="button ghost small file-upload">
                              Upload image
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleBannerFileChange(
                                    banner.tempId,
                                    event
                                  )
                                }
                              />
                            </label>
                            {typeof bannerUploadProgress[banner.tempId] ===
                            "number" ? (
                              <div className="helper-text">
                                Uploading {bannerUploadProgress[banner.tempId]}%
                              </div>
                            ) : null}
                            {banner.imageUrl ? (
                              <button
                                className="button ghost small"
                                type="button"
                                onClick={() =>
                                  handleUpdateBanner(
                                    banner.tempId,
                                    "imageUrl",
                                    ""
                                  )
                                }
                              >
                                Remove image
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="button danger small"
                            type="button"
                            onClick={() => handleRemoveBanner(banner.tempId)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {!bannerDraft.length ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="empty-state">
                          <div className="empty-title">No banners yet</div>
                          <div className="empty-subtitle">
                            Add a banner to start advertising on the home screen.
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
          <div className="helper-text">
            Banners are ordered by the Order value (lowest first). Only active
            banners show in the app.
          </div>
          </div>
        </div>
      ) : null}

      {activeTab === "games" && canEditAccess ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h3>Game catalog</h3>
              <p className="helper-text">
                Manage games, formats, and entry types for tournaments and creators.
              </p>
            </div>
            <div className="inline-actions">
              <button
                className="button ghost small"
                type="button"
                onClick={refreshGames}
                disabled={gamesLoading}
              >
                Refresh
              </button>
              <button
                className="button ghost small"
                type="button"
                onClick={handleResetGames}
                disabled={!isGamesDirty || savingGames || hasUploadingIcon}
              >
                Reset
              </button>
              <button
                className="button primary small"
                type="button"
                onClick={handleSaveGames}
                disabled={!isGamesDirty || savingGames || hasUploadingIcon}
              >
                {savingGames ? "Saving..." : "Save"}
              </button>
              <button
                className="button small"
                type="button"
                onClick={handleAddGame}
              >
                Add game
              </button>
            </div>
          </div>
          {gamesLoadError ? (
            <div className="alert error">{gamesLoadError}</div>
          ) : null}
          {gamesError ? <div className="alert error">{gamesError}</div> : null}
          {gamesLoading && !gameDraft.length ? (
            <div className="helper-text">Loading game catalog...</div>
          ) : (
            <div className="table-wrap">
              <table className="table game-catalog-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Format</th>
                    <th>Entry Type</th>
                    <th>Order</th>
                    <th>Icon</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gameDraft.map((game) => {
                    const fallbackIcon = games.find(
                      (item) => item.id === game.id
                    )?.icon;
                    const previewSrc = game.imageUrl || fallbackIcon || "";
                    return (
                    <tr key={game.tempId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={game.active !== false}
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "active",
                              event.target.checked
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={game.id}
                          placeholder="game-id"
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "id",
                              event.target.value
                            )
                          }
                          disabled={!game.isNew}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={game.name}
                          placeholder="Game name"
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "name",
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={game.format}
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "format",
                              event.target.value
                            )
                          }
                        >
                          {FORMAT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={game.entryType}
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "entryType",
                              event.target.value
                            )
                          }
                        >
                          {ENTRY_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={game.order}
                          onChange={(event) =>
                            handleUpdateGame(
                              game.tempId,
                              "order",
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="icon-cell">
                          <div className="icon-preview">
                            {previewSrc ? (
                              <img src={previewSrc} alt="" />
                            ) : (
                              <span className="icon-placeholder">
                                {game.name?.slice(0, 1) || "?"}
                              </span>
                            )}
                          </div>
                          <div className="icon-actions">
                            <label className="button ghost small file-upload">
                              Upload icon
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleIconFileChange(game.tempId, event)
                                }
                              />
                            </label>
                            {typeof iconUploadProgress[game.tempId] ===
                            "number" ? (
                              <div className="helper-text">
                                Uploading {iconUploadProgress[game.tempId]}%
                              </div>
                            ) : null}
                            {game.imageUrl ? (
                              <button
                                className="button ghost small"
                                type="button"
                                onClick={() =>
                                  handleUpdateGame(game.tempId, "imageUrl", "")
                                }
                              >
                                Remove icon
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="button danger small"
                            type="button"
                            onClick={() => handleRemoveGame(game.tempId)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {!gameDraft.length ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="empty-state">
                          <div className="empty-title">No games yet</div>
                          <div className="empty-subtitle">
                            Add a game to start configuring the catalog.
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
          <div className="helper-text">
            Existing game IDs are locked to prevent breaking references. Use the
            Active toggle to hide a game without removing it.
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Settings;
